import { useState, useEffect, useCallback, useRef } from 'react'
import { createPortal } from 'react-dom'
import {
  Search, X, Loader2, Building, Database, User, Phone, Mail,
  MapPin, IndianRupee, Layers, AlertTriangle,
  Trash2, Pencil, Plus, RefreshCw, Info, LayoutGrid,
  List, ChevronRight, FileSpreadsheet, Activity,
  Upload, FileCode2, Download, Eye, ChevronDown, ChevronUp, FileMinus
} from 'lucide-react'
import { customersService } from '@/services/firebase/customers'
import { customerXmlService, type CustomerXmlFile } from '@/services/firebase/customerXml'
import { useAuthStore } from '@/store/authStore'
import type { BsnlCustomer } from '@/types'
import toast from 'react-hot-toast'

import CustomerForm from '../components/CustomerForm'


// ─── Delete Confirmation Modal ────────────────────────────────────────────────
const DeleteConfirm = ({ name, onConfirm, onCancel, deleting }: { name: string, onConfirm: () => void, onCancel: () => void, deleting: boolean }) => createPortal(
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
    <div className="bg-white rounded-3xl w-full max-w-sm p-7 flex flex-col items-center text-center gap-5 shadow-2xl">
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-red-50 border border-red-200">
        <AlertTriangle size={28} className="text-red-500" />
      </div>
      <div>
        <p className="font-extrabold text-gray-900 text-lg">Delete Customer?</p>
        <p className="text-sm text-gray-500 mt-2 leading-relaxed">
          <span className="font-bold text-gray-800">{name}</span> will be permanently removed from the FTTH database.
        </p>
      </div>
      <div className="flex gap-3 w-full">
        <button onClick={onCancel} className="flex-1 py-3 border border-gray-200 rounded-2xl text-sm font-bold text-gray-600 hover:bg-gray-50 transition-colors">
          Cancel
        </button>
        <button onClick={onConfirm} disabled={deleting}
          className="flex-1 py-3 text-white rounded-2xl text-sm font-black transition-all disabled:opacity-60 flex items-center justify-center gap-2 active:scale-[0.98]"
          style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', boxShadow: '0 4px 16px rgba(239,68,68,0.3)' }}>
          {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
          {deleting ? 'Deleting...' : 'Delete'}
        </button>
      </div>
    </div>
  </div>,
  document.body
)

// ─── XML Manager Component ────────────────────────────────────────────────────
const XmlManager = ({ customerId, customerName }: { customerId: string; customerName: string }) => {
  const [files, setFiles] = useState<CustomerXmlFile[]>([])
  const [loadingFiles, setLoadingFiles] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [viewingFile, setViewingFile] = useState<CustomerXmlFile | null>(null)
  const [xmlContent, setXmlContent] = useState<string | null>(null)
  const [loadingContent, setLoadingContent] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<CustomerXmlFile | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [collapsed, setCollapsed] = useState(true)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const loadFiles = useCallback(async () => {
    setLoadingFiles(true)
    try {
      const res = await customerXmlService.list(customerId)
      if (res.success && res.data) {
        setFiles(res.data)
      } else {
        console.error('[XmlManager] Failed to list files:', res.error)
        toast.error(res.error ?? 'Failed to load XML files')
      }
    } catch (err) {
      console.error('[XmlManager] Unexpected error loading XML files:', err)
      toast.error('Unexpected error loading XML files')
    } finally {
      setLoadingFiles(false)
    }
  }, [customerId])

  useEffect(() => {
    if (!collapsed) {
      loadFiles()
    }
  }, [collapsed, loadFiles])

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.xml')) {
      toast.error('Only .xml files can be uploaded.')
      return
    }
    setUploading(true)
    setUploadProgress(0)
    const res = await customerXmlService.upload(customerId, file, (pct) => setUploadProgress(Math.round(pct)))
    setUploading(false)
    if (res.success) {
      toast.success(`${file.name} uploaded successfully!`)
      loadFiles()
    } else {
      toast.error(res.error ?? 'Upload failed')
    }
    // Reset so same file can be re-uploaded if needed
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleView = async (f: CustomerXmlFile) => {
    setViewingFile(f)
    setXmlContent(null)
    setLoadingContent(true)
    const res = await customerXmlService.fetchContent(f.downloadUrl)
    setLoadingContent(false)
    if (res.success && res.data) {
      setXmlContent(res.data)
    } else {
      toast.error('Could not load XML content')
    }
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    const res = await customerXmlService.delete(deleteTarget.path)
    setDeleting(false)
    if (res.success) {
      toast.success(`${deleteTarget.name} deleted`)
      setDeleteTarget(null)
      loadFiles()
    } else {
      toast.error(res.error ?? 'Delete failed')
    }
  }

  // Pretty-print XML
  const formatXml = (xml: string): string => {
    try {
      let formatted = ''
      let indent = 0
      const nodes = xml.replace(/>\s*</g, '>|<').split('|')
      for (const node of nodes) {
        if (node.match(/^<\/\w/)) indent -= 2
        formatted += ' '.repeat(Math.max(0, indent)) + node + '\n'
        if (node.match(/^<\w[^>]*[^/]>$/) && !node.match(/^<.+\/>/) && !node.match(/^<!/) && !node.match(/^<\?/)) {
          indent += 2
        }
      }
      return formatted.trim()
    } catch {
      return xml
    }
  }

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Section Header */}
      <button
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <FileCode2 size={16} className="text-violet-500" />
          <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">XML Backup Files</h3>
          {files.length > 0 && (
            <span className="text-[10px] font-black px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 border border-violet-200">
              {files.length}
            </span>
          )}
        </div>
        {collapsed ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronUp size={14} className="text-gray-400" />}
      </button>

      {!collapsed && (
        <div className="px-5 pb-5 space-y-3 border-t border-gray-100">
          {/* Upload button */}
          <div className="pt-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xml,application/xml,text/xml"
              className="hidden"
              onChange={handleFileChange}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full flex items-center justify-center gap-2 py-2.5 border-2 border-dashed border-violet-300 rounded-xl text-xs font-bold text-violet-600 hover:bg-violet-50 hover:border-violet-400 transition-all disabled:opacity-50 active:scale-[0.98]"
            >
              {uploading ? (
                <><Loader2 size={13} className="animate-spin" /> Uploading... {uploadProgress}%</>
              ) : (
                <><Upload size={13} /> Upload XML File</>  
              )}
            </button>
            {uploading && (
              <div className="mt-2 h-1 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-violet-500 transition-all rounded-full"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            )}
          </div>

          {/* File List */}
          {loadingFiles ? (
            <div className="flex items-center justify-center py-5">
              <Loader2 size={18} className="animate-spin text-violet-400" />
            </div>
          ) : files.length === 0 ? (
            <div className="text-center py-6">
              <FileMinus size={22} className="text-gray-200 mx-auto mb-2" />
              <p className="text-xs text-gray-400">No XML backups uploaded yet for <span className="font-bold text-gray-600">{customerName}</span></p>
            </div>
          ) : (
            <ul className="space-y-2">
              {files.map(f => (
                <li key={f.path} className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-50 border border-gray-100 hover:border-violet-200 hover:bg-violet-50/30 transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center shrink-0">
                    <FileCode2 size={14} className="text-violet-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-gray-800 truncate">{f.name}</p>
                    <p className="text-[10px] text-gray-400 font-mono">
                      {customerXmlService.formatSize(f.size)} &middot; {new Date(f.updatedAt).toLocaleDateString('en-IN')}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => handleView(f)}
                      title="Preview XML"
                      className="w-7 h-7 flex items-center justify-center rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-500 transition-colors"
                    >
                      <Eye size={12} />
                    </button>
                    <button
                      onClick={() => customerXmlService.triggerDownload(f)}
                      title="Download XML"
                      className="w-7 h-7 flex items-center justify-center rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-600 transition-colors"
                    >
                      <Download size={12} />
                    </button>
                    <button
                      onClick={() => setDeleteTarget(f)}
                      title="Delete XML"
                      className="w-7 h-7 flex items-center justify-center rounded-lg bg-red-50 hover:bg-red-100 text-red-500 transition-colors"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* XML Viewer Modal */}
      {viewingFile && createPortal(
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-3 bg-black/70 backdrop-blur-sm">
          <div className="bg-[#0d1117] rounded-3xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden border border-white/10">
            {/* Viewer Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-[#161b22] shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
                  <FileCode2 size={15} className="text-violet-400" />
                </div>
                <div>
                  <p className="text-sm font-bold text-white">{viewingFile.name}</p>
                  <p className="text-[10px] text-gray-500">{customerXmlService.formatSize(viewingFile.size)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => customerXmlService.triggerDownload(viewingFile)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600/80 hover:bg-emerald-600 text-white text-xs font-bold rounded-xl transition-colors"
                >
                  <Download size={11} /> Download
                </button>
                <button
                  onClick={() => { setViewingFile(null); setXmlContent(null) }}
                  className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 text-gray-300 transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
            {/* Viewer Body */}
            <div className="overflow-auto flex-1 p-5">
              {loadingContent ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 size={24} className="animate-spin text-violet-400" />
                </div>
              ) : (
                <pre className="text-[11px] text-green-400 font-mono leading-relaxed whitespace-pre-wrap break-all">
                  {xmlContent ? formatXml(xmlContent) : 'No content available'}
                </pre>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete Confirm for XML File */}
      {deleteTarget && createPortal(
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-3xl w-full max-w-xs p-6 flex flex-col items-center text-center gap-4 shadow-2xl">
            <div className="w-12 h-12 rounded-xl bg-red-50 border border-red-200 flex items-center justify-center">
              <AlertTriangle size={22} className="text-red-500" />
            </div>
            <div>
              <p className="font-extrabold text-gray-900">Delete XML File?</p>
              <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">
                <span className="font-bold text-gray-800">{deleteTarget.name}</span> will be permanently deleted from storage.
              </p>
            </div>
            <div className="flex gap-3 w-full">
              <button
                onClick={() => setDeleteTarget(null)}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl text-xs font-bold text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 py-2.5 text-white rounded-xl text-xs font-black bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
              >
                {deleting && <Loader2 size={12} className="animate-spin" />}
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

// ─── Customer Details Drawer (Slide-out Panel) ──────────────────────────────────
interface DetailsDrawerProps {
  cust: BsnlCustomer
  onClose: () => void
  onEdit: () => void
  onDelete: () => void
}

const DetailsDrawer = ({ cust, onClose, onEdit, onDelete }: DetailsDrawerProps) => {
  const initials = cust.customerName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
  
  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div 
        className="bg-white w-full max-w-xl h-full shadow-2xl flex flex-col animate-slide-in"
        onClick={e => e.stopPropagation()}
      >
        {/* Banner header */}
        <div className="relative shrink-0 overflow-hidden text-white"
          style={{ background: 'linear-gradient(160deg, #030a18 0%, #071428 50%, #0a1e3a 100%)' }}>
          
          {/* Decorative glowing background elements */}
          <div className="absolute -top-12 -left-12 w-56 h-56 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, #3b82f640 0%, transparent 65%)', filter: 'blur(32px)' }} />
          <div className="absolute -bottom-8 -right-8 w-40 h-40 rounded-full pointer-events-none"
            style={{ background: 'radial-gradient(circle, #06b6d425 0%, transparent 65%)', filter: 'blur(24px)' }} />

          <div className="absolute top-0 left-0 right-0 h-[3px] bg-blue-500" />
          
          <div className="px-6 pt-6 pb-5 relative z-10 flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center text-white font-black text-xl shrink-0 shadow-lg shadow-blue-500/25"
                style={{ background: 'linear-gradient(135deg, #2563eb, #06b6d4)' }}>
                {initials}
              </div>
              <div className="min-w-0">
                <h2 className="font-black text-white text-lg leading-tight truncate max-w-xs">{cust.customerName}</h2>
                <p className="text-xs text-blue-300 font-bold mt-1">Service Number: {cust.serviceNumber}</p>
                <p className="text-[10px] font-mono text-slate-400 mt-0.5 uppercase tracking-widest">Building: {cust.building || 'N/A'}</p>
              </div>
            </div>
            
            <button onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-2xl bg-white/10 hover:bg-white/20 transition-all border border-white/12 active:scale-95">
              <X size={16} className="text-white" />
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50">
          
          {/* Main Configuration Card */}
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 space-y-4">
            <div className="flex items-center gap-2 border-b border-gray-100 pb-2">
              <Activity size={16} className="text-blue-500" />
              <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">Connection Configuration</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Broadband User ID</p>
                <p className="text-sm font-bold text-gray-800 font-mono select-all bg-slate-50 px-2 py-1 rounded border border-gray-100 inline-block mt-0.5">{cust.bbUserId}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">OLT IP Address</p>
                <p className="text-sm font-bold text-gray-800 font-mono select-all bg-slate-50 px-2 py-1 rounded border border-gray-100 inline-block mt-0.5">{cust.oltIp}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">VLAN ID</p>
                <p className="text-sm font-bold text-indigo-700 font-mono mt-0.5">{cust.vlan !== null ? cust.vlan : 'N/A'}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Port</p>
                <p className="text-sm font-bold text-indigo-700 font-mono mt-0.5">{cust.port !== null ? cust.port : 'N/A'}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">VoIP Password</p>
                <p className="text-sm font-semibold text-gray-600 font-mono mt-0.5">{cust.voipPassword || 'None'}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Exchange Code</p>
                <p className="text-sm font-bold text-gray-700 mt-0.5">{cust.exchangeCode}</p>
              </div>
            </div>
          </div>

          {/* Billing Plan Info */}
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 space-y-4">
            <div className="flex items-center gap-2 border-b border-gray-100 pb-2">
              <IndianRupee size={16} className="text-emerald-500" />
              <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">Plan & Billing</h3>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Subscription Plan</p>
                <p className="text-sm font-bold text-gray-900 mt-0.5">{cust.subscriptionPlan}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Monthly FMC</p>
                  <p className="text-base font-extrabold text-emerald-600 mt-0.5">Rs. {cust.fmc}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Plan Period</p>
                  <span className="inline-block text-[10px] font-black px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-100 mt-1">{cust.planPeriod}</span>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Billing Account No.</p>
                  <p className="text-xs font-bold text-gray-700 font-mono mt-0.5">{cust.billingAccountNo || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Service Type</p>
                  <p className="text-xs font-semibold text-gray-500 mt-0.5">{cust.subServiceType}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Details */}
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 space-y-4">
            <div className="flex items-center gap-2 border-b border-gray-100 pb-2">
              <User size={16} className="text-indigo-500" />
              <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">Customer Profile Details</h3>
            </div>
            <div className="space-y-3.5">
              <div className="flex items-start gap-3">
                <Phone size={14} className="text-gray-400 mt-1 shrink-0" />
                <div>
                  <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider">Phone / Mobile</p>
                  <p className="text-sm font-bold text-gray-800">+91 {cust.mobile}</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <Mail size={14} className="text-gray-400 mt-1 shrink-0" />
                <div>
                  <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider">Email ID</p>
                  <p className="text-sm font-semibold text-gray-700 truncate max-w-sm">{cust.email || 'No email provided'}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <MapPin size={14} className="text-gray-400 mt-1 shrink-0" />
                <div>
                  <p className="text-[9px] text-gray-400 font-bold uppercase tracking-wider">Installation Address</p>
                  <p className="text-xs font-semibold text-gray-700 leading-relaxed mt-0.5">{cust.address}</p>
                </div>
              </div>
            </div>
          </div>

          {/* System metadata */}
          <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100 space-y-3">
            <div className="flex justify-between items-center text-xs">
              <span className="text-gray-400 font-medium">Activation Date</span>
              <span className="text-gray-700 font-bold font-mono">{cust.activationDate}</span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="text-gray-400 font-medium">Category Code</span>
              <span className="text-gray-700 font-semibold bg-gray-100 px-2 py-0.5 rounded">{cust.category}</span>
            </div>
          </div>

          {/* XML File Manager */}
          <XmlManager customerId={cust.id} customerName={cust.customerName} />
        </div>

        {/* Action Panel Footer */}
        <div className="p-4 border-t border-gray-100 shrink-0 bg-white flex gap-3">
          <button 
            onClick={onEdit}
            className="flex-1 py-3 bg-blue-50 hover:bg-blue-100 text-blue-600 font-bold rounded-2xl text-sm flex items-center justify-center gap-1.5 transition-colors"
          >
            <Pencil size={14} /> Edit Record
          </button>
          <button 
            onClick={onDelete}
            className="flex-1 py-3 bg-red-50 hover:bg-red-100 text-red-500 font-bold rounded-2xl text-sm flex items-center justify-center gap-1.5 transition-colors"
          >
            <Trash2 size={14} /> Delete Customer
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Main View Component ─────────────────────────────────────────────────────
const BsnlCustomerListPage = () => {
  const { user } = useAuthStore()
  const isAdmin = user?.role === 'admin'

  const [customers, setCustomers] = useState<BsnlCustomer[]>([])
  const [loading, setLoading] = useState(true)
  const [seeding, setSeeding] = useState(false)
  const [search, setSearch] = useState('')
  
  // Filtering
  const [buildingFilter, setBuildingFilter] = useState('all')
  const [planFilter, setPlanFilter] = useState('all')
  
  // UI states
  const [selectedCust, setSelectedCust] = useState<BsnlCustomer | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editTarget, setEditTarget] = useState<BsnlCustomer | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<BsnlCustomer | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await customersService.list()
      if (res.success && res.data) {
        setCustomers(res.data)
      } else {
        toast.error(res.error ?? 'Failed to load customer records')
      }
    } catch (error) {
      toast.error('An error occurred during fetch')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleSeed = async (force = false) => {
    setSeeding(true)
    try {
      const res = await customersService.seedBsnlCustomers(force)
      if (res.success) {
        toast.success(res.message ?? 'Import complete')
        load()
      } else {
        toast.error(res.error ?? 'Import failed')
      }
    } catch (err) {
      toast.error('Import error occurred')
    } finally {
      setSeeding(false)
    }
  }

  const handleExportToExcel = () => {
    if (filtered.length === 0) {
      toast.error('No customer records to export.')
      return
    }

    const headers = [
      'Category', 'Exchange Code', 'Service Number', 'Sub Service Type', 
      'Subscription Plan', 'Plan Period', 'Customer', 'Billing Account No.', 
      'Mobile', 'Email', 'Address', 'FMC', 'OLT IP', 'BB USER ID', 
      'Voip Password', 'Vlan', 'Port', 'Building', 'Activation Date'
    ]
    
    const rows = filtered.map(c => [
      c.category,
      c.exchangeCode,
      c.serviceNumber,
      c.subServiceType,
      c.subscriptionPlan,
      c.planPeriod,
      c.customerName,
      c.billingAccountNo,
      c.mobile,
      c.email || '',
      c.address,
      c.fmc,
      c.oltIp,
      c.bbUserId,
      c.voipPassword || '',
      c.vlan !== null ? c.vlan : '',
      c.port !== null ? c.port : '',
      c.building,
      c.activationDate
    ])
    
    // Convert to CSV string, escaping double quotes and wrapping fields in quotes
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(val => {
        const str = String(val ?? '');
        return `"${str.replace(/"/g, '""')}"`;
      }).join(','))
    ].join('\r\n')
    
    // Add UTF-8 Byte Order Mark (BOM) to make sure Excel opens it with correct encoding (UTF-8)
    const BOM = '\uFEFF'
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    
    link.setAttribute('href', url)
    link.setAttribute('download', `BSNL_FTTH_Customer_Data_${new Date().toLocaleDateString('en-IN').replace(/\//g, '-')}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    toast.success(`Successfully exported ${filtered.length} records!`)
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      const res = await customersService.remove(deleteTarget.id)
      if (res.success) {
        toast.success('Customer deleted successfully')
        setDeleteTarget(null)
        if (selectedCust?.id === deleteTarget.id) setSelectedCust(null)
        load()
      } else {
        toast.error(res.error ?? 'Delete failed')
      }
    } catch (err) {
      toast.error('Delete error occurred')
    } finally {
      setDeleting(false)
    }
  }

  // Pre-calculate filter option arrays
  const buildings = Array.from(new Set(customers.map(c => c.building).filter(Boolean))).sort()
  const plans = Array.from(new Set(customers.map(c => c.subscriptionPlan).filter(Boolean))).sort()

  const filtered = customers.filter(c => {
    const q = search.toLowerCase()
    const matchesSearch = 
      !q ||
      c.customerName.toLowerCase().includes(q) ||
      c.serviceNumber.toLowerCase().includes(q) ||
      String(c.mobile).includes(q) ||
      c.bbUserId.toLowerCase().includes(q) ||
      c.oltIp.toLowerCase().includes(q) ||
      c.address.toLowerCase().includes(q)

    const matchesBuilding = buildingFilter === 'all' || c.building === buildingFilter
    const matchesPlan = planFilter === 'all' || c.subscriptionPlan === planFilter

    return matchesSearch && matchesBuilding && matchesPlan
  })

  // KPI Calculations
  const activePlansCount = new Set(customers.map(c => c.subscriptionPlan)).size
  const uniqueBuildingsCount = new Set(customers.map(c => c.building).filter(Boolean)).size
  const totalFMC = customers.reduce((sum, c) => sum + (c.fmc || 0), 0)

  return (
    <div className="space-y-5 animate-page-enter">
      {/* Hero card */}
      <div className="relative overflow-hidden rounded-3xl text-white"
        style={{ background: 'linear-gradient(135deg, #050d1f 0%, #0d2142 55%, #102e5c 100%)' }}>
        
        {/* Glow orbs */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-blue-500/20 blur-[45px]" />
          <div className="absolute bottom-0 left-0 w-48 h-32 rounded-full bg-cyan-500/10 blur-[35px]" />
        </div>

        <div className="relative z-10 p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 rounded-full px-3 py-1 mb-2">
              <FileSpreadsheet size={10} className="text-cyan-300" />
              <span className="text-[10px] text-blue-200 font-semibold uppercase tracking-wider">BSNL EXCEL DATABASE</span>
            </div>
            <h1 className="text-[22px] font-black leading-tight">Customer Database</h1>
            <p className="text-blue-300/70 text-sm mt-0.5">
              Showing imported BSNL customers &amp; technical details
            </p>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap shrink-0">
            <div className="flex items-center rounded-xl p-1 gap-0.5 bg-white/10">
              <button 
                onClick={() => setViewMode('list')} 
                className={`p-1.5 rounded-lg transition-all ${viewMode === 'list' ? 'bg-white/20 text-white' : 'text-white/40'}`}
                title="List View"
              >
                <List size={14} />
              </button>
              <button 
                onClick={() => setViewMode('grid')} 
                className={`p-1.5 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-white/20 text-white' : 'text-white/40'}`}
                title="Grid View"
              >
                <LayoutGrid size={14} />
              </button>
            </div>

            {isAdmin && (
              <>
                <button
                  onClick={handleExportToExcel}
                  className="flex items-center gap-1.5 px-3.5 py-2 text-white border border-emerald-500/20 bg-emerald-600/85 hover:bg-emerald-600 rounded-2xl text-[13px] font-semibold transition-all active:scale-95 shadow-sm"
                  title="Export current view to Excel file"
                >
                  <FileSpreadsheet size={13} />
                  Export Excel
                </button>
                <button
                  onClick={() => handleSeed(true)}
                  disabled={seeding}
                  className="flex items-center gap-1.5 px-3.5 py-2 text-white border border-white/15 bg-white/10 rounded-2xl text-[13px] font-semibold transition-all hover:bg-white/15 active:scale-95 disabled:opacity-50"
                >
                  {seeding ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                  Re-sync Excel
                </button>
                <button 
                  onClick={() => { setEditTarget(null); setShowForm(true) }}
                  className="flex items-center gap-1.5 px-3.5 py-2 text-white rounded-2xl text-[13px] font-black transition-all active:scale-95"
                  style={{ background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', boxShadow: '0 4px 14px rgba(37,99,235,0.4)' }}
                >
                  <Plus size={14} /> Add Customer
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Customers', val: customers.length, color: 'text-blue-600', bg: 'bg-blue-50/50', icon: User },
          { label: 'Unique Buildings', val: uniqueBuildingsCount, color: 'text-indigo-600', bg: 'bg-indigo-50/50', icon: Building },
          { label: 'Broadband Plans', val: activePlansCount, color: 'text-purple-600', bg: 'bg-purple-50/50', icon: Layers },
          { label: 'Total Revenue (FMC)', val: `₹${totalFMC}`, color: 'text-emerald-600', bg: 'bg-emerald-50/50', icon: IndianRupee }
        ].map((card, i) => (
          <div key={i}
            className="bg-white border border-gray-200/80 rounded-2xl p-4 shadow-sm flex items-center gap-3 animate-card-enter"
            style={{ '--stagger': i } as React.CSSProperties}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${card.bg}`}>
              <card.icon size={18} className={card.color} />
            </div>
            <div>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">{card.label}</p>
              <p className="text-lg font-black text-gray-800 leading-tight mt-0.5">{card.val}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Database Empty Alert / Manual Seeder */}
      {!loading && customers.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-3xl p-5 flex flex-col md:flex-row items-center gap-4 text-center md:text-left justify-between">
          <div className="flex gap-3 items-start">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0 border border-amber-200">
              <Info size={20} className="text-amber-700" />
            </div>
            <div>
              <p className="font-extrabold text-amber-900 text-sm">Customer Database is empty</p>
              <p className="text-xs text-amber-700/80 leading-relaxed mt-0.5">
                The Excel file `BSNL Customer Data Updated May 27 2026.xlsx` in the public folder contains 100 customer records. Click the Sync button to export all details directly.
              </p>
            </div>
          </div>
          <button
            onClick={() => handleSeed(false)}
            disabled={seeding}
            className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white font-bold text-xs rounded-xl shadow-sm transition-all shrink-0 flex items-center gap-2"
          >
            {seeding ? <Loader2 size={13} className="animate-spin" /> : <Database size={13} />}
            Import 100 BSNL Customers
          </button>
        </div>
      )}

      {/* Filter / Search Bar */}
      <div className="bg-white rounded-3xl border border-gray-200 p-4 shadow-sm space-y-3">
        <div className="flex items-center gap-2 border border-gray-200 rounded-2xl px-4 py-2.5 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-400 transition-all">
          <Search size={14} className="text-gray-400 shrink-0" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, service no, user ID, address, phone, OLT IP..."
            className="flex-1 text-sm outline-none text-gray-900 placeholder-gray-400 bg-transparent"
          />
          {search && <button onClick={() => setSearch('')}><X size={13} className="text-gray-400 hover:text-gray-600" /></button>}
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Building:</span>
            <select
              value={buildingFilter}
              onChange={e => setBuildingFilter(e.target.value)}
              className="text-xs font-semibold text-gray-700 bg-slate-50 border border-gray-200 rounded-lg px-2 py-1 outline-none focus:border-blue-400"
            >
              <option value="all">All Buildings</option>
              {buildings.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>

          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Plan:</span>
            <select
              value={planFilter}
              onChange={e => setPlanFilter(e.target.value)}
              className="text-xs font-semibold text-gray-700 bg-slate-50 border border-gray-200 rounded-lg px-2 py-1 outline-none focus:border-blue-400 max-w-xs"
            >
              <option value="all">All Plans</option>
              {plans.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          {/* Reset filter button */}
          {(buildingFilter !== 'all' || planFilter !== 'all' || search) && (
            <button
              onClick={() => { setBuildingFilter('all'); setPlanFilter('all'); setSearch('') }}
              className="text-xs font-bold text-blue-600 hover:text-blue-800 transition-colors flex items-center gap-1"
            >
              Clear Filters
            </button>
          )}

          <div className="ml-auto text-xs text-gray-400 font-medium">
            Found <span className="font-extrabold text-gray-700">{filtered.length}</span> / {customers.length} records
          </div>
        </div>
      </div>

      {/* Main List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-28 gap-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center bg-blue-50/50">
            <Loader2 size={22} className="text-blue-500 animate-spin" />
          </div>
          <p className="text-sm text-gray-400 font-medium">Fetching customer records...</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-3xl border border-gray-200 py-24 text-center flex flex-col items-center justify-center p-6 gap-4">
          <div className="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-300">
            <Database size={24} />
          </div>
          <div>
            <p className="font-extrabold text-gray-700 text-sm">No customers match search criteria</p>
            <p className="text-xs text-gray-400 mt-1 max-w-xs mx-auto">Try refining your keyword query or resetting active building/plan dropdown filters.</p>
          </div>
        </div>
      ) : viewMode === 'list' ? (
        /* List Layout Table */
        <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-gray-200 text-gray-400 font-black uppercase tracking-wider text-[10px]">
                  <th className="py-3 px-4">Customer Name</th>
                  <th className="py-3 px-4">Service Number</th>
                  <th className="py-3 px-4">Building</th>
                  <th className="py-3 px-4">Broadband ID</th>
                  <th className="py-3 px-4">OLT IP Address</th>
                  <th className="py-3 px-4">VLAN / Port</th>
                  <th className="py-3 px-4 text-right">FMC</th>
                  <th className="py-3 px-4"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 font-medium text-gray-700">
                {filtered.map((cust, rowIdx) => (
                  <tr 
                    key={cust.id} 
                    onClick={() => setSelectedCust(cust)}
                    className="hover:bg-slate-50/70 transition-colors cursor-pointer group animate-row-enter"
                    style={{ '--stagger': Math.min(rowIdx, 12) } as React.CSSProperties}
                  >
                    <td className="py-3.5 px-4">
                      <p className="font-extrabold text-gray-900 group-hover:text-blue-600 transition-colors text-sm">{cust.customerName}</p>
                      <p className="text-[10px] text-gray-400 mt-0.5">+91 {cust.mobile}</p>
                    </td>
                    <td className="py-3.5 px-4 font-mono font-bold text-gray-800">{cust.serviceNumber}</td>
                    <td className="py-3.5 px-4">
                      <span className="inline-block px-2.5 py-0.5 rounded-full bg-slate-100 text-gray-600 font-bold text-[10px] uppercase">
                        {cust.building || 'N/A'}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 font-mono text-gray-500">{cust.bbUserId}</td>
                    <td className="py-3.5 px-4 font-mono text-gray-500">{cust.oltIp}</td>
                    <td className="py-3.5 px-4 font-mono">
                      {cust.vlan !== null ? cust.vlan : '—'} <span className="text-gray-300">/</span> {cust.port !== null ? cust.port : '—'}
                    </td>
                    <td className="py-3.5 px-4 text-right font-black text-emerald-600 text-sm">₹{cust.fmc}</td>
                    <td className="py-3.5 px-4 text-right">
                      <ChevronRight size={14} className="text-gray-300 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all inline-block" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Grid Layout Cards */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((cust, cardIdx) => {
            const initials = cust.customerName.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
            return (
              <div 
                key={cust.id} 
                onClick={() => setSelectedCust(cust)}
                className="bg-white rounded-3xl border border-gray-200/80 p-4 shadow-sm hover:shadow-md cursor-pointer transition-all hover:-translate-y-0.5 flex flex-col group relative overflow-hidden animate-card-enter"
                style={{ '--stagger': Math.min(cardIdx, 12) } as React.CSSProperties}
              >
                {/* Header info */}
                <div className="flex gap-3 items-start mb-3">
                  <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-white font-black text-sm shrink-0 bg-gradient-to-br from-blue-500 to-cyan-500 shadow-sm"
                    style={{ background: 'linear-gradient(135deg, #3b82f6, #06b6d4)' }}>
                    {initials}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-extrabold text-gray-900 group-hover:text-blue-600 transition-colors text-sm truncate leading-tight">{cust.customerName}</h3>
                    <p className="text-[10px] text-gray-400 font-mono mt-0.5">Service: {cust.serviceNumber}</p>
                  </div>
                  <ChevronRight size={14} className="text-gray-300 group-hover:text-blue-500 shrink-0" />
                </div>
                
                {/* Technical data table */}
                <div className="space-y-1.5 py-2.5 border-t border-b border-gray-100 flex-1">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-400 font-medium">BB User:</span>
                    <span className="font-bold text-gray-700 font-mono truncate max-w-[150px]">{cust.bbUserId}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-400 font-medium">OLT IP:</span>
                    <span className="font-bold text-gray-600 font-mono">{cust.oltIp}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-400 font-medium">Vlan / Port:</span>
                    <span className="font-bold text-gray-700 font-mono">{cust.vlan !== null ? cust.vlan : '—'} / {cust.port !== null ? cust.port : '—'}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-gray-400 font-medium">Building:</span>
                    <span className="font-bold text-gray-700 truncate max-w-[150px]">{cust.building || 'N/A'}</span>
                  </div>
                </div>

                {/* FMC Revenue footer */}
                <div className="flex justify-between items-center mt-3 text-xs pt-1">
                  <span className="text-gray-400 font-medium">Plan FMC:</span>
                  <span className="font-extrabold text-emerald-600 text-sm">₹{cust.fmc}</span>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Details Slide Drawer */}
      {selectedCust && (
        <DetailsDrawer
          cust={selectedCust}
          onClose={() => setSelectedCust(null)}
          onEdit={() => { setEditTarget(selectedCust); setShowForm(true) }}
          onDelete={() => setDeleteTarget(selectedCust)}
        />
      )}

      {/* Edit Form Modal */}
      {showForm && (
        <CustomerForm
          initial={editTarget}
          onClose={() => { setShowForm(false); setEditTarget(null) }}
          onSaved={() => { setShowForm(false); setEditTarget(null); load(); if (selectedCust) setSelectedCust(null) }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteTarget && (
        <DeleteConfirm
          name={deleteTarget.customerName}
          deleting={deleting}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  )
}

export default BsnlCustomerListPage
